import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import server from '../../../api/server';
import Card from '../ui/Card';
import AvatarCircle from '../ui/AvatarCircle';
import ConsentExport from './ConsentExport';
import { generateConsentDocument } from './consentDocument';
import { EyeIcon } from '../ui/icons';

// Redesign of newadmin/PrivacyConsent.js — lists each applicant's versioned
// consent records via ConsentExport (same /consent/* endpoints).
const PrivacyConsentCard = () => {
    const applicants = useSelector((s) => s.admin.active_application?.application?.applicants);
    const [printingAll, setPrintingAll] = useState(false);
    const [printAllError, setPrintAllError] = useState(null);

    const clients = applicants
        ? Object.keys(applicants).map((key) => ({ id: key, ...applicants[key] }))
        : [];

    // Applicants who can actually have a consent record on file.
    const printableClients = clients.filter((c) => c.firebase_id);

    // One document containing every applicant's latest consent, each in its own
    // labelled section. Printing per-applicant produced documents that were hard
    // to tell apart on a joint application, so this prints them side by side.
    const printAllConsents = async () => {
        setPrintingAll(true);
        setPrintAllError(null);
        try {
            const responses = await Promise.all(
                printableClients.map((client) =>
                    server
                        .post(`/consent/export/${client.firebase_id}`, {})
                        .then((res) => ({ client, data: res.data }))
                        .catch(() => ({ client, data: null }))
                )
            );

            const entries = responses.map(({ client, data }) => ({
                // Fall back to the applicant record so a failed lookup still
                // shows up in the document as "no consent recorded".
                user: data?.user || {
                    name: client.name,
                    email: client.email,
                    application_id: client.application_id,
                },
                version: data?.version || null,
                versions_count: data?.versions_count ?? 0,
            }));

            const withMeta = responses.find((r) => r.data);
            generateConsentDocument(entries, {
                exported_at: withMeta?.data?.exported_at || new Date(),
                exported_by: withMeta?.data?.exported_by || 'N/A',
            });
        } catch (err) {
            console.error('Combined consent export error:', err);
            setPrintAllError('Failed to build the combined consent record.');
        } finally {
            setPrintingAll(false);
        }
    };

    return (
        <Card
            title="Privacy Consent Management"
            subtitle="Snapshots of each client's consent. Open any to view / save as PDF."
            icon={<EyeIcon className="h-5 w-5" />}
            actions={
                <div className="flex items-center gap-3">
                    {printableClients.length > 1 ? (
                        <button
                            type="button"
                            onClick={printAllConsents}
                            disabled={printingAll}
                            className="inline-flex items-center rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#5B3FE0] hover:bg-[#EFEAFE] transition-colors disabled:opacity-50"
                        >
                            {printingAll
                                ? 'Building...'
                                : `Print all ${printableClients.length} consents`}
                        </button>
                    ) : null}
                    <span className="text-xs text-[#6B7280]">3-month consent renewal required</span>
                </div>
            }
        >
            {clients.length === 0 ? (
                <p className="text-sm text-[#6B7280] text-center py-6">
                    No clients found for this application.
                </p>
            ) : (
                <div className="space-y-6">
                    {clients.map((client, index) => (
                        <div key={client.id || index}>
                            <div className="flex items-center gap-3 mb-3">
                                <AvatarCircle name={client.name} size="md" />
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-[#1F2937] truncate">
                                        {client.name || 'Unknown Client'}
                                    </p>
                                    <p className="text-xs text-[#6B7280] truncate">
                                        {client.email || 'No email provided'}
                                        {client.mobile ? ` | ${client.mobile}` : ''}
                                    </p>
                                </div>
                            </div>

                            {client.firebase_id ? (
                                <ConsentExport userId={client.firebase_id} />
                            ) : (
                                <p className="text-sm text-[#6B7280] pl-1">
                                    This client has not created an account yet.
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {printAllError ? (
                <p className="mt-3 text-sm text-[#DC2626]">{printAllError}</p>
            ) : null}

            <div
                className="mt-6 rounded-xl px-4 py-3 text-xs"
                style={{ backgroundColor: '#EFEAFE', color: '#5B3FE0' }}
            >
                <strong>Privacy Compliance:</strong> Every consent is versioned and never deleted. Export
                PDF records include the full policy text, data-sharing disclosure, the Equifax credit-check
                answer, timestamps, IP addresses, user agents, and expiry dates. All consents expire after
                3 months and require renewal.
            </div>
        </Card>
    );
};

export default PrivacyConsentCard;
